<script setup>
import { reactive, ref } from "vue";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import axios from "@/axios.js";
import debounce from "lodash.debounce";
import { showToast } from '@/eventBus.js';

// Day.js
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("America/Sao_Paulo");

const props = defineProps({
    services: { type: Array, required: true },
    nameEquips: { type: Array, required: true }, 
});

const names = ["Concluido", "Visita", "Garantia", "Cancelado", "Apoio Braçal"];

// Eventos para o pai
const emit = defineEmits(["update-service", "delete-service"]);

const editingKey = ref(null); // string | null
const editBuffer = reactive({}); 

const rowKey = (type, i, r = null) => (r === null ? `one:${i}` : `old:${i}:${r}`);
const isEditing = (type, i, r = null) => editingKey.value === rowKey(type, i, r);

function startEdit(type, i, dataFlat, r = null) {
    editingKey.value = rowKey(type, i, r);
    for (const k of Object.keys(editBuffer)) delete editBuffer[k];
    Object.assign(editBuffer, { ...dataFlat });
};

function cancelEdit() {
    editingKey.value = null;
    for (const k of Object.keys(editBuffer)) delete editBuffer[k];
};

function saveEdit(service) {
    const companyId = localStorage.getItem("idCompany");

    axios.put(`/api/servicos/edit/${companyId}`, service)
        .then(response => {
            showToast(response.data.message || 'Dados atualizados no grid.');
            emit("update-service");
            cancelEdit();
        })
        .catch(error => {
            console.error("Erro ao salvar o serviço:", error);
            showToast(error?.response?.data?.message || "Erro persistente no salvamento.");
        });
};

function deleteService(type, index, retIndex, payload) {
    emit("delete-service", { type, index, retIndex, service: payload });
};

function formatDate(date) {
    return dayjs(date).tz("America/Sao_Paulo").format("DD/MM/YYYY");
};

const handleEquipamentoChange = debounce((buffer) => {
    const companyId = localStorage.getItem("idCompany");
    const url = `/api/servicos/search/warranty/${companyId}`;
    const params = {
        numeroServico: buffer.numeroServico,
        nomeEquipamento: buffer.nomeEquipamento,
    };
    axios
        .get(url, { params })
        .then((response) => {
            const data = response.data?.data || {};
            buffer.nomeEquipamento = data.nomeEquipamento ?? buffer.nomeEquipamento;
            if (data.valor != null) buffer.valor = data.valor;
        })
        .catch((error) => {
            showToast(error?.response?.data || "Conflito na recuperação do componente.");
        });
}, 400);

function groupEndClass(service, hasOld, r, lastIdx) {
    if (!hasOld && r === null) {
        return "group-end";
    }
    if (hasOld && r !== null && r === lastIdx) {
        return "group-end";
    }
    return "";
}
</script>

<template>
    <div class="data-grid-wrapper">
        <div class="table-scroll-container">
            <table class="v2-data-table">
                <thead>
                    <tr>
                        <th style="width: 8%">Num. SO</th>
                        <th style="width: 7%">QRA</th>
                        <th style="width: 7%">Viatura</th>
                        <th style="width: 8%">Data Ext.</th>
                        <th style="width: 25%">Relatório Técnico (Descrição)</th>
                        <th style="width: 10%">Módulo</th>
                        <th style="width: 10%">Perfil (Portal)</th>
                        <th style="width: 8%">Custo (R$)</th>
                        <th style="width: 12%">Observação Interna</th>
                        <th style="width: 5%">Operação</th>
                    </tr>

                </thead>

                <tbody>
                    <template v-for="(service, i) in services" :key="service?.oneService?._id ?? `grp-${i}`">
                        
                        <!-- Linha Principal -->
                        <tr class="master-row" :class="groupEndClass(service, Array.isArray(service?.oldServices) && service.oldServices.length > 0, null, null)" v-if="!service?.oneService?._hidden">
                            
                            <td class="so-cell">
                                <span class="so-badge">{{ service.oneService.numeroServico }}</span>
                            </td>

                            <td class="regular-cell">
                                <span v-if="!isEditing('one', i)">{{ service.oneService.codigoQRA }}</span>
                                <input v-else class="form-control-grid" v-model="service.oneService.codigoQRA" />
                            </td>

                            <td class="regular-cell">
                                <span>{{ service.oneService.codigoViatura }}</span>
                            </td>

                            <td class="regular-cell">
                                <span class="date-chip">{{ formatDate(service.oneService.data) }}</span>
                            </td>

                            <!-- Scrollable Text Cell to prevent wide breaking -->
                            <td class="text-cell">
                                <div v-if="!isEditing('one', i)" class="text-clamp-box">
                                    {{ service.oneService.mensagemPrestador }}
                                </div>
                                <textarea v-else v-model="service.oneService.mensagemPrestador" class="form-control-grid area"></textarea>
                            </td>

                            <td class="regular-cell">
                                <span v-if="!isEditing('one', i)" class="pill-badge type-blue">{{ service.oneService.nomeEquipamento }}</span>
                                <div v-else class="select-wrapper">
                                    <select class="form-select-grid" v-model="service.oneService.nomeEquipamento" @change="handleEquipamentoChange(service.oneService)">
                                        <option :value="service.oneService.nomeEquipamento">{{ service.oneService.nomeEquipamento }}</option>
                                        <option v-for="equip in names" :key="`eq-${equip}`" :value="equip">{{ equip }}</option>
                                    </select>
                                </div>
                            </td>

                            <td class="regular-cell">
                                <span v-if="!isEditing('one', i)">{{ service.oneService.nomePortal }}</span>
                                <div v-else class="select-wrapper">
                                    <select class="form-select-grid" v-model="service.oneService.nomePortal">
                                        <option :value="service.oneService.nomePortal">{{ service.oneService.nomePortal }}</option>
                                        <option v-for="equip in nameEquips" :key="`np-${equip}`" :value="equip">{{ equip }}</option>
                                    </select>
                                </div>
                            </td>

                            <td class="regular-cell fw-bold">
                                <span v-if="!isEditing('one', i)">{{ Number(service.oneService.valor ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                                <input v-else type="number" class="form-control-grid" v-model.number="service.oneService.valor" />
                            </td>

                            <td class="text-cell">
                                <div v-if="!isEditing('one', i)" class="text-clamp-box small">
                                   {{ service.oneService.observacao || '--' }}
                                </div>
                                <textarea v-else v-model="service.oneService.observacao" class="form-control-grid area"></textarea>
                            </td>

                            <td class="actions-cell">
                                <div class="btn-group-grid">
                                    <template v-if="isEditing('one', i)">
                                        <button @click="saveEdit(service.oneService)" class="action-ico save" title="Salvar"><i class="bi bi-check-lg"></i></button>
                                        <button @click="cancelEdit" class="action-ico cancel" title="Cancelar"><i class="bi bi-x-lg"></i></button>
                                    </template>
                                    <template v-else>
                                        <button @click="startEdit('one', i, service.oneService)" class="action-ico edit" title="Editar"><i class="bi bi-pencil-fill"></i></button>
                                        <button @click="deleteService('one', i, null, service.oneService)" class="action-ico delete" title="Ocultar"><i class="bi bi-trash3-fill"></i></button>
                                    </template>
                                </div>
                            </td>
                        </tr>

                        <!-- Linha Filha (Retorno de Serviço) -->
                        <tr v-for="(ret, r) in (service.oldServices ?? [])" :key="ret?._id ?? `ret-${i}-${r}`" class="child-row" :class="[groupEndClass(service, (service.oldServices ?? []).length > 0, r, (service.oldServices ?? []).length - 1), { 'd-none': ret._hidden }]">
                            
                            <td class="so-cell has-indent">
                                <div class="indent-tree">
                                   <i class="bi bi-arrow-return-right"></i>
                                   <span class="so-badge child">{{ ret.numeroServico }}</span>
                                </div>
                            </td>

                            <td class="regular-cell">
                                <span v-if="!isEditing('old', i, r)">{{ ret.codigoQRA }}</span>
                                <input v-else class="form-control-grid" v-model="ret.codigoQRA" />
                            </td>

                            <td class="regular-cell">
                                <span>{{ ret.codigoViatura }}</span>
                            </td>

                            <td class="regular-cell">
                                <span class="date-chip">{{ formatDate(ret.data) }}</span>
                            </td>

                            <td class="text-cell">
                                <div v-if="!isEditing('old', i, r)" class="text-clamp-box">
                                    {{ ret.mensagemPrestador }}
                                </div>
                                <textarea v-else v-model="ret.mensagemPrestador" class="form-control-grid area"></textarea>
                            </td>

                            <td class="regular-cell">
                                <span v-if="!isEditing('old', i, r)" class="pill-badge type-blue">{{ ret.nomeEquipamento }}</span>
                                <div v-else class="select-wrapper">
                                    <select class="form-select-grid" v-model="ret.nomeEquipamento" @change="handleEquipamentoChange(ret)">
                                        <option :value="ret.nomeEquipamento">{{ ret.nomeEquipamento }}</option>
                                        <option v-for="equip in names" :key="`eq-r-${equip}`" :value="equip">{{ equip }}</option>
                                    </select>
                                </div>
                            </td>

                            <td class="regular-cell">
                                <span v-if="!isEditing('old', i, r)">{{ ret.nomePortal }}</span>
                                <div v-else class="select-wrapper">
                                    <select class="form-select-grid" v-model="ret.nomePortal">
                                        <option :value="ret.nomePortal">{{ ret.nomePortal }}</option>
                                        <option v-for="equip in nameEquips" :key="`np-r-${equip}`" :value="equip">{{ equip }}</option>
                                    </select>
                                </div>
                            </td>

                            <td class="regular-cell fw-bold text-muted">
                                <span v-if="!isEditing('old', i, r)">{{ Number(ret.valor ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                                <input v-else type="number" class="form-control-grid" v-model.number="ret.valor" />
                            </td>

                            <td class="text-cell">
                                <div v-if="!isEditing('old', i, r)" class="text-clamp-box small">
                                    {{ ret.observacao || '--' }}
                                </div>
                                <textarea v-else v-model="ret.observacao" class="form-control-grid area"></textarea>
                            </td>

                            <td class="actions-cell">
                                <div class="btn-group-grid">
                                    <template v-if="isEditing('old', i, r)">
                                        <button @click="saveEdit(ret)" class="action-ico save" title="Salvar"><i class="bi bi-check-lg"></i></button>
                                        <button @click="cancelEdit" class="action-ico cancel" title="Cancelar"><i class="bi bi-x-lg"></i></button>
                                    </template>
                                    <template v-else>
                                        <button @click="startEdit('old', i, ret, r)" class="action-ico edit" title="Editar"><i class="bi bi-pencil-fill"></i></button>
                                        <button @click="deleteService('old', i, r, ret)" class="action-ico delete" title="Ocultar"><i class="bi bi-trash3-fill"></i></button>
                                    </template>
                                </div>
                            </td>

                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style scoped>
/* Dashboard Container Params */
.data-grid-wrapper {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.02);
  overflow: hidden;
}

.table-scroll-container {
  width: 100%;
  max-height: calc(100vh - 280px);
  overflow-y: auto;
  overflow-x: auto;
}

/* Base SaaS Table */
.v2-data-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1400px;
  background: #FFFFFF;
}

/* Sticky modern Headers */
.v2-data-table thead th {
  position: sticky;
  top: 0;
  background: #F8FAFC;
  color: #475569;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 1rem;
  border-bottom: 2px solid #E2E8F0;
  z-index: 10;
  text-align: left;
}

/* Generic Cell Settings */
.v2-data-table tbody td {
  padding: 1rem;
  vertical-align: middle; /* Kept middle as baseline but text-block dictates wrap */
  font-size: 0.9rem;
  color: #1E293B;
  border-bottom: 1px solid #F1F5F9;
}

/* Master Row styling */
.master-row {
  transition: background 0.2s ease;
}
.master-row:hover { background: #F8FAFC; }

/* Group Boundary line */
.group-end td {
  border-bottom: 2px solid #E2E8F0;
}

/* Child Row (Return Service) */
.child-row {
  background-color: #FAFAFA;
}
.child-row:hover {
  background-color: #F1F5F9;
}
.child-row td {
  color: #64748B; /* Slight dim text to indicate inheritance */
}

/* Text Wrapping Strategies (Anti-overwriting mechanism) */
.text-cell {
  vertical-align: top; /* Text cells align top to prevent odd stretching */
}

/* This is the magic box that fixes standard tables breaking over text */
.text-clamp-box {
  max-height: 72px; /* Limits physical size */
  overflow-y: auto; /* Activates pure inner scroll when full */
  white-space: pre-wrap; /* Keeps breaks */
  word-wrap: break-word; /* Prevents overflow string without spaces */
  line-height: 1.4;
  padding-right: 4px;
}
.text-clamp-box.small {
  font-size: 0.85rem;
  color: #64748B;
}

/* Badges / Chips decorators */
.so-badge {
  display: inline-block;
  background: #1E293B;
  color: #FFF;
  font-weight: 700;
  font-size: 0.8rem;
  padding: 0.2rem 0.6rem;
  border-radius: 6px;
}
.so-badge.child {
  background: #94A3B8;
  font-size: 0.75rem;
}

.date-chip {
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
}

.pill-badge {
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.25rem 0.6rem;
  border-radius: 20px;
}
.pill-badge.type-blue {
  background: #EFF6FF;
  color: #2563EB;
}

/* Indent mechanism */
.indent-tree {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.indent-tree i {
  color: #94A3B8;
  font-size: 1.1rem;
  opacity: 0.7;
}

/* Editor forms inside grid */
.form-control-grid {
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem;
  border: 1px solid #DF523A;
  border-radius: 6px;
  font-size: 0.85rem;
  outline: none;
  font-family: inherit;
}
.form-control-grid.area {
  min-height: 60px;
  resize: vertical;
}

.select-wrapper {
  position: relative;
}
.form-select-grid {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #DF523A;
  border-radius: 6px;
  font-size: 0.85rem;
  background: transparent;
  outline: none;
}

/* Actions buttons modern */
.btn-group-grid {
  display: flex;
  gap: 0.5rem;
}

.action-ico {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #475569;
}
.action-ico.edit { background: #F1F5F9; }
.action-ico.edit:hover { background: #3B82F6; color: #FFF; }

.action-ico.delete { background: #FEF2F2; color: #EF4444; }
.action-ico.delete:hover { background: #DC2626; color: #FFF; }

.action-ico.save { background: #DCFCE7; color: #16A34A; }
.action-ico.save:hover { background: #16A34A; color: #FFF; }

.action-ico.cancel { background: #FDE68A; color: #B45309; }
.action-ico.cancel:hover { background: #F59E0B; color: #FFF; }

.fw-bold { font-weight: 700; }
.text-muted { color: #64748B !important; }

/* Custom light scrollbar for text cells */
.text-clamp-box::-webkit-scrollbar {
  width: 4px;
}
.text-clamp-box::-webkit-scrollbar-thumb {
  background-color: #CBD5E1;
  border-radius: 10px;
}
</style>
